import { UploadOutlined } from "@ant-design/icons";
import { openLocalFile } from "@/utils/openDocument";
import { Button, Form, FormInstance, Input, message, Upload, UploadFile } from "antd";

interface ClientDocumentUploadFieldProps {
  form: FormInstance;
  fieldName: string;
  label: string;
  required?: boolean;
  fileList: UploadFile[];
  onChange: (fileList: UploadFile[]) => void;
  /** Called when the user clicks an already-saved (server-side) file name. */
  onPreviewExisting?: () => void;
}

/**
 * A single "upload one document" field used for the extra NetSuite client documents
 * (PAN card, GST certificate, Agreement, MSME certificate). Mirrors the existing ID-proof
 * upload pattern: the actual file is tracked in local state (uploaded separately after the
 * client is created/updated), while a hidden Form.Item carries the filename so antd's built-in
 * form validation can enforce "required" the same way as any other field.
 */
export default function ClientDocumentUploadField({
  form,
  fieldName,
  label,
  required,
  fileList,
  onChange,
  onPreviewExisting,
}: ClientDocumentUploadFieldProps) {
  return (
    <div className="flex flex-col pb-4" data-doc-field={fieldName}>
      <label className="text-sm mb-2">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <Upload
        fileList={fileList}
        // Remove icon hidden for already-saved documents until the delete behaviour is decided.
        showUploadList={{ showRemoveIcon: !fileList[0]?.uid?.startsWith("-existing-") }}
        beforeUpload={(file) => {
          const isValidType = ["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(file.type);
          if (!isValidType) {
            message.error("You can only upload PDF, JPG, or PNG files!");
            return Upload.LIST_IGNORE;
          }

          const isValidSize = file.size / 1024 / 1024 < 5;
          if (!isValidSize) {
            message.error("File must be smaller than 5MB!");
            return Upload.LIST_IGNORE;
          }

          onChange([
            {
              uid: file.uid || "-1",
              name: file.name,
              status: "done",
              originFileObj: file,
            } as UploadFile,
          ]);
          form.setFieldsValue({ [fieldName]: file.name });
          return false;
        }}
        onPreview={(file) => (file.originFileObj ? openLocalFile(file) : onPreviewExisting?.())}
        onRemove={() => {
          onChange([]);
          form.setFieldsValue({ [fieldName]: undefined });
        }}
        maxCount={1}
      >
        <Button icon={<UploadOutlined />} className="w-full">
          Upload {label}
        </Button>
      </Upload>
      <Form.Item
        name={fieldName}
        hidden
        rules={required ? [{ required: true, message: `${label} is required` }] : []}
      >
        <Input />
      </Form.Item>
    </div>
  );
}
